<template>
    <zen-slide-in :isOpen="isOpen" title="Create Swap" @close="$emit('close')">
        <template #body>
            <div class="space-y-1 mb-6">
                <p class="text-sm text-taupe-600">
                    Temporarily swap one environment into another. The target
                    serves exactly what the source serves, rules and rollouts
                    included, for every flag. The source is copied when you
                    create the swap, so later changes to it don't affect a
                    running swap. The target keeps its own configuration and
                    reverts automatically when the swap ends.
                </p>
            </div>

            <div class="space-y-5">
                <div>
                    <label
                        for="swap-source"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Source environment <span class="text-red-600">*</span>
                    </label>
                    <div class="mt-2">
                        <zen-select
                            id="swap-source"
                            v-model="form.sourceUlid"
                            :options="sourceOptions"
                            option-label="name"
                            option-value="ulid"
                            placeholder="Choose a source environment"
                        />
                    </div>
                </div>

                <div>
                    <label
                        for="swap-target"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Target environment <span class="text-red-600">*</span>
                    </label>
                    <div class="mt-2">
                        <zen-select
                            id="swap-target"
                            v-model="form.targetKey"
                            :options="targetOptions"
                            option-label="name"
                            option-value="key"
                            placeholder="Choose a target environment"
                        />
                    </div>
                    <p
                        v-if="excludedTargetCount > 0"
                        class="mt-1.5 text-xs text-taupe-500"
                        data-testid="swap-protected-note"
                    >
                        Protected environments and environments that don't allow
                        swaps can't be targets.
                    </p>
                </div>

                <div v-if="selectedSource && selectedTarget">
                    <div
                        class="block text-sm font-medium leading-6 text-taupe-900 mb-2"
                    >
                        Changes on {{ selectedTarget.name }}
                    </div>

                    <p
                        v-if="diffLoading"
                        class="text-sm text-taupe-500"
                        data-testid="swap-diff-loading"
                    >
                        Loading the preview...
                    </p>

                    <p
                        v-else-if="diff && diff.length === 0"
                        class="text-sm text-taupe-500"
                        data-testid="swap-diff-empty"
                    >
                        {{ selectedSource.name }} has no published flags to
                        swap.
                    </p>

                    <template v-else-if="diff">
                        <p
                            class="text-sm text-taupe-700"
                            data-testid="swap-diff-summary"
                        >
                            <template v-if="changedRows.length === 0">
                                {{ selectedTarget.name }} already serves the
                                same as {{ selectedSource.name }} for all
                                {{ diff.length }} flags, so an environment swap
                                would change nothing.
                            </template>
                            <template v-else>
                                {{ changedRows.length }} of
                                {{ diff.length }} flags will serve differently.
                                <span
                                    v-if="unchangedCount > 0"
                                    class="text-taupe-500"
                                >
                                    {{ unchangedCount }} already
                                    {{
                                        unchangedCount === 1
                                            ? 'matches'
                                            : 'match'
                                    }}
                                    {{ selectedSource.name }}.
                                </span>
                            </template>
                        </p>

                        <ul
                            v-if="changedRows.length > 0"
                            class="mt-3 divide-y divide-taupe-200 rounded-md border border-taupe-200 bg-white px-4"
                            data-testid="swap-diff"
                        >
                            <li
                                v-for="row in changedRows"
                                :key="row.flag_ulid"
                                class="flex items-start gap-3 py-3"
                                data-testid="swap-diff-row"
                            >
                                <!-- The name keeps at least 40% of the row and the values get at most 60%, so a long value wraps in its own column instead of squeezing the name out. wrap-anywhere rather than break-words: only it lowers the min-content width, so a long unbroken token (a URL) can't prop the slide-in open past a phone viewport. -->
                                <div
                                    class="min-w-0 shrink-0 grow basis-2/5 wrap-anywhere"
                                    data-testid="swap-diff-flag"
                                >
                                    <span
                                        class="block text-sm font-medium text-taupe-900"
                                    >
                                        {{ row.flag_name }}
                                    </span>
                                    <span
                                        class="block font-mono text-xs text-taupe-500"
                                    >
                                        {{ row.flag_key }}
                                    </span>
                                </div>
                                <div
                                    class="min-w-0 max-w-3/5 wrap-anywhere text-right text-xs font-mono"
                                    data-testid="swap-diff-values"
                                >
                                    <template v-if="valueChanged(row)">
                                        <span
                                            class="block text-taupe-500 line-through"
                                        >
                                            {{ formatValue(row.target_value) }}
                                        </span>
                                        <span class="block text-taupe-900">
                                            {{ formatValue(row.source_value) }}
                                        </span>
                                    </template>
                                    <span
                                        v-if="rulesChanged(row)"
                                        class="block text-taupe-700"
                                        data-testid="swap-diff-rules"
                                    >
                                        Rules:
                                        {{ row.target_rule_count }} &rarr;
                                        {{ row.source_rule_count }}
                                    </span>
                                    <span
                                        v-if="rolloutChanged(row)"
                                        class="block text-taupe-700"
                                        data-testid="swap-diff-rollout"
                                    >
                                        Rollout:
                                        {{
                                            formatRollout(
                                                row.target_rollout_percentage
                                            )
                                        }}
                                        &rarr;
                                        {{
                                            formatRollout(
                                                row.source_rollout_percentage
                                            )
                                        }}
                                    </span>
                                </div>
                            </li>
                        </ul>
                    </template>
                </div>

                <div>
                    <label
                        for="swap-duration"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Duration
                    </label>
                    <div class="mt-2">
                        <zen-select
                            id="swap-duration"
                            v-model="form.hours"
                            :options="durations"
                            option-label="label"
                            option-value="hours"
                        />
                    </div>
                    <p
                        class="mt-1.5 text-xs text-taupe-500"
                        data-testid="swap-revert-at"
                    >
                        Reverts automatically at
                        <zen-date-time :value="revertsAt" />.
                    </p>
                </div>
            </div>
        </template>
        <template #buttons>
            <zen-button @click="$emit('close')" variant="secondary">
                Close
            </zen-button>
            <zen-button
                @click="confirm"
                variant="primary"
                :disabled="!canConfirm"
                :processing="processing"
            >
                Create swap
            </zen-button>
        </template>
    </zen-slide-in>
</template>

<script setup>
import { computed, reactive, watch } from 'vue'

/**
 * Swap durations offered in the picker. Hours keep the expiry math exact.
 */
const durations = [
    { label: '1 hour', hours: 1 },
    { label: '4 hours', hours: 4 },
    { label: '1 day', hours: 24 },
    { label: '7 days', hours: 168 },
]

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true,
    },
    environments: {
        type: Array,
        required: true,
    },
    // Rows from the preview endpoint, or null until a source and target are chosen.
    diff: {
        type: Array,
        default: null,
    },
    diffLoading: {
        type: Boolean,
        default: false,
    },
    // Target preselected when the modal opens from a specific environment.
    initialTargetKey: {
        type: String,
        default: null,
    },
    processing: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['close', 'confirm', 'preview'])

/**
 * @typedef {Object} SwapForm
 * @property {string|null} sourceUlid
 * @property {string|null} targetKey
 * @property {number} hours
 */

/** @type {SwapForm} */
const form = reactive({
    sourceUlid: null,
    targetKey: null,
    hours: 24,
})

/**
 * Protected environments and environments without overlays can never be a
 * swap target. The server enforces this too; the picker just never offers them.
 */
const isOverlayable = (environment) =>
    environment.can_overlay === true &&
    environment.confirmation_required !== true

const selectedSource = computed(
    () => props.environments.find((env) => env.ulid === form.sourceUlid) ?? null
)

const selectedTarget = computed(
    () => props.environments.find((env) => env.key === form.targetKey) ?? null
)

const sourceOptions = computed(() =>
    props.environments.filter((env) => env.key !== selectedTarget.value?.key)
)

const targetOptions = computed(() =>
    props.environments.filter(
        (env) => isOverlayable(env) && env.ulid !== form.sourceUlid
    )
)

const excludedTargetCount = computed(
    () => props.environments.filter((env) => !isOverlayable(env)).length
)

const expiryFor = (hours) =>
    new Date(Date.now() + hours * 60 * 60 * 1000).toISOString()

const revertsAt = computed(() => expiryFor(form.hours))

// A swap replaces the whole environment, so the rows only describe it: nothing is picked flag by flag.
const rows = computed(() => (Array.isArray(props.diff) ? props.diff : []))

const changedRows = computed(() => rows.value.filter((row) => row.changed))

const unchangedCount = computed(
    () => rows.value.length - changedRows.value.length
)

const valueChanged = (row) => row.source_value_ulid !== row.target_value_ulid

const rulesChanged = (row) => row.source_rule_count !== row.target_rule_count

const rolloutChanged = (row) =>
    (row.source_rollout_percentage ?? null) !==
    (row.target_rollout_percentage ?? null)

// Nothing to confirm until the preview shows something would change.
const canConfirm = computed(
    () =>
        selectedSource.value !== null &&
        selectedTarget.value !== null &&
        changedRows.value.length > 0 &&
        !props.processing
)

const formatRollout = (percentage) =>
    percentage === null || percentage === undefined ? 'none' : `${percentage}%`

const formatValue = (value) => {
    if (value === null || value === undefined) {
        return 'unset'
    }

    return typeof value === 'string' ? value : JSON.stringify(value)
}

const reset = () => {
    form.sourceUlid = null
    form.targetKey = null
    form.hours = 24
}

// Ask the screen for a fresh diff whenever the pair changes.
watch(
    () =>
        /** @type {[string|null, string|null]} */ ([
            form.sourceUlid,
            form.targetKey,
        ]),
    ([sourceUlid, targetKey]) => {
        // typeof rather than truthiness: the inferred types here are too narrow for a truthy check to be meaningful.
        if (typeof sourceUlid === 'string' && typeof targetKey === 'string') {
            emit('preview', { sourceUlid, targetKey })
        }
    }
)

watch(
    () => props.isOpen,
    (open) => {
        if (open) {
            form.targetKey = props.initialTargetKey
        } else {
            reset()
        }
    }
)

const confirm = () => {
    if (!canConfirm.value) {
        return
    }

    emit('confirm', {
        targetEnvironmentKey: selectedTarget.value.key,
        payload: {
            source_environment_ulid: selectedSource.value.ulid,
            expires_at: expiryFor(form.hours),
        },
    })
}

defineExpose({
    form,
    canConfirm,
    changedRows,
    unchangedCount,
    targetOptions,
    sourceOptions,
    excludedTargetCount,
})
</script>
